// useTimers — the signed-in person's visible timers, live.
// One-click timers (docs/specs/timers-one-click-spec.md, Track C).
//
// Reads lib/timerStore.listMyTimers for the TOKEN's uid (never AuthContext:
// a stale context user must not show or act on another person's timers).
// Re-reads when:
//   • a `storage` event names a timer slot or an owner record (another window
//     or tab changed one), the token (sign-in change), or key null ("Reset UI
//     preferences" — lib/uiPrefs.js — and other bulk clears);
//   • this window's own store writes fire the same-window TIMER_SLOT_EVENT.
// Ticks once a second ONLY while one of my timers is (effectively) ticking —
// or while `forceTick` is set — so an idle Layout does not re-render every
// second; a render is skipped entirely when nothing visible changed (owner
// heartbeats every 5 s would otherwise wake every consumer).
// Runs the debounced reconciler (RECONCILE_DEBOUNCE_MS) on mount, on slot
// storage events, on key-null events and on this window's own
// TIMER_SLOT_EVENT, in every window that mounts it with a uid: concurrent
// runs in several windows write identical values.
//
// Returns { timers, current, anyTicking, count, now, uid, refresh }.
//   timers   — timerStore Rows, ticking first, then by last activity
//   current  — timers[0] (the running one, else the most recently active)
//   refresh  — re-read now (e.g. after an action that dispatches nothing)
// Options: { store, uid, tick = true, forceTick = false, reconcile = true }.
//   uid omitted → the token's uid on every read; tick:false → events only
//   (e.g. Layout's ⏱ pulse needs anyTicking, not a 1 s clock).

import { useCallback, useEffect, useRef, useState } from 'react'
import timerStore, { RECONCILE_DEBOUNCE_MS, TIMER_SLOT_EVENT, tokenUid } from '../lib/timerStore'
import { OWNER_PREFIX, isTimerSlotKey } from '../lib/timerSlots'

const TOKEN_KEY = 'lextrack_token'

function signatureOf (u, timers) {
  try {
    return JSON.stringify([u, timers.map(r => [
      r.key, r.ticking, r.elapsed, r.cls, r.ownerState, r.heldElsewhere, r.heldHere,
      r.claimed, r.postUncertain, r.futureStart, r.longRunning, r.slot
    ])])
  } catch {
    return String(Math.random())
  }
}

export function useTimers ({ store = timerStore, uid, tick = true, forceTick = false, reconcile = true } = {}) {
  const storeRef = useRef(store)
  storeRef.current = store
  const uidPropRef = useRef(uid)
  uidPropRef.current = uid
  const reconcileRef = useRef(reconcile)
  reconcileRef.current = reconcile

  const readUid = useCallback(() => {
    const p = uidPropRef.current
    if (p !== undefined) return p == null || p === '' ? null : String(p)
    return tokenUid()
  }, [])

  const compute = useCallback(() => {
    const u = readUid()
    const now = Date.now()
    let timers = []
    try { timers = u ? storeRef.current.listMyTimers(u, { now }) : [] } catch { timers = [] }
    return { uid: u, now, timers, sig: signatureOf(u, timers) }
  }, [readUid])

  const [state, setState] = useState(compute)

  const refresh = useCallback(() => {
    const next = compute()
    setState(prev => (prev.sig === next.sig ? prev : next))
  }, [compute])

  useEffect(() => {
    let debounce = null
    const scheduleReconcile = () => {
      if (!reconcileRef.current) return
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => {
        debounce = null
        const u = readUid()
        if (u) {
          try { storeRef.current.reconcile(u) } catch { /* never break the page */ }
        }
        refresh()
      }, RECONCILE_DEBOUNCE_MS)
    }
    const onStorage = (e) => {
      const k = e ? e.key : undefined
      if (k === null) { refresh(); scheduleReconcile(); return }
      if (typeof k !== 'string') return
      if (isTimerSlotKey(k)) { refresh(); scheduleReconcile(); return }
      if (k.startsWith(OWNER_PREFIX) || k === TOKEN_KEY) refresh()
    }
    // This window's own writes too: a transition here can leave two of my
    // timers ticking in storage (a stale read, an old window's resume) — the
    // reconciler is deterministic and idempotent, so a run with nothing to
    // do writes nothing and fires nothing.
    const onLocal = () => { refresh(); scheduleReconcile() }
    window.addEventListener('storage', onStorage)
    window.addEventListener(TIMER_SLOT_EVENT, onLocal)
    refresh()
    scheduleReconcile()
    return () => {
      if (debounce) clearTimeout(debounce)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(TIMER_SLOT_EVENT, onLocal)
    }
  }, [refresh, readUid])

  // A changed uid prop re-reads at once.
  useEffect(() => { refresh() }, [uid, store, refresh])

  const anyTicking = state.timers.some(r => r.ticking)
  useEffect(() => {
    if (!tick || !(anyTicking || forceTick)) return undefined
    const id = setInterval(refresh, 1000)
    return () => clearInterval(id)
  }, [tick, anyTicking, forceTick, refresh])

  return {
    timers: state.timers,
    current: state.timers[0] || null,
    anyTicking,
    count: state.timers.length,
    now: state.now,
    uid: state.uid,
    refresh
  }
}

export default useTimers
